import { 
  Component, 
  EventEmitter, 
  Input, 
  Output, 
  ElementRef, 
  ViewChild 
} from '@angular/core';

@Component({
  selector: 'kt-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent {
  @Input() label: string = 'Attach Documents';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() maxFiles: number = 1;
  @Input() accept: string = 'image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  @Output() filesChanged = new EventEmitter<File[]>();

  @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>;

  files: FileWithPreview[] = [];
  isHovering: boolean = false;
  isFocused: boolean = false;
  errorMessage: string = '';

  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  shouldLabelFloat(): boolean {
    return this.files.length > 0 || this.isFocused;
  }

  onDropZoneClick(): void {
    if (!this.disabled && this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isHovering = true;
  }

  onDragLeave(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isHovering = false;
  }

  onDrop(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isHovering = false;

    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      this.handleFiles(Array.from(droppedFiles));
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
      input.value = ''; // Reset input
    }
  }

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;
  }

  private handleFiles(newFiles: File[]): void {
    this.errorMessage = '';
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of newFiles) {
      // Validate file size
      if (file.size > this.MAX_FILE_SIZE) {
        errors.push(`${file.name} exceeds 5MB limit`);
        continue;
      }

      // Validate file type
      // const isValidType = this.accept.includes(file.type);

      // if (!isValidType) {
      //   errors.push(`${file.name} is not a valid file type`);
      //   continue;
      // }

      validFiles.push(file);
    }

    // Set error message if any files were rejected
    if (errors.length > 0) {
      this.errorMessage = errors.length === 1
        ? errors[0]
        : `${errors.length} files were rejected. Only images, PDF, and Word documents under 5MB are allowed.`;
    }

    // Process valid files
    if (validFiles.length > 0) {
      this.processFiles(validFiles);
    }
  }

  private processFiles(validFiles: File[]): void {
    const hasExceededMaxFiles = this.files.length + validFiles.length > this.maxFiles;
    if (hasExceededMaxFiles) {
      this.errorMessage = `Maksimal file yang dapat diunggah adalah ${this.maxFiles}.`;
      return;
    }
    
    validFiles.forEach(file => {
      const fileWithPreview: FileWithPreview = file as FileWithPreview;

      // Generate preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          fileWithPreview.preview = e.target.result as string;
        };
        reader.readAsDataURL(file);
      }

      this.files.push(fileWithPreview);
    });

    this.emitFilesChanged();
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.errorMessage = '';
    this.emitFilesChanged();
  }

  private emitFilesChanged(): void {
    this.filesChanged.emit(this.files);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}

interface FileWithPreview extends File {
  preview?: string;
}
